/**
 * @file BinSelectionBar.tsx
 * @description Нижняя полоса выбора ячеек: «Выбрано N · Печать этикеток»
 *   (phase19.22 шаг 5; правила warehouse-UI — sticky bottom action-bar).
 *
 * Появляется, пока что-то отмечено, и на это время заменяет полосу «Стеллаж /
 * Ячейка» на телефоне — две полосы друг над другом съели бы пол-экрана.
 * `position: sticky` в конце потока, как у `BinsActions`: не накрывает
 * последнюю карточку. На `≥lg` — карточкой в ширину содержимого.
 */

import { Printer, X } from "lucide-react";

import { Button } from "../../ui/button";
import { stickyBarProps } from "../../ui/sticky-bar";
import { pluralRu } from "../../../lib/plural";

interface Props {
  count: number;
  /** Подпись охвата: «стеллаж A целиком». */
  scopeNote?: string | null;
  onClear: () => void;
  onPrint: () => void;
}

export function BinSelectionBar({ count, scopeNote, onClear, onPrint }: Props) {
  return (
    // Метка нижней полосы: над ней встаёт кнопка «Сообщения» (SC64).
    <div {...stickyBarProps} className="sticky bottom-0 z-20 -mx-4 flex items-center gap-2 border-t border-on-surface/8 bg-surface-lowest p-3 sm:-mx-6 md:-mx-8 lg:mx-0 lg:rounded-xl lg:border">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-medium tabular-nums">
          Выбрано {count} {pluralRu(count, ["ячейка", "ячейки", "ячеек"])}
        </span>
        {scopeNote ? <span className="truncate text-xs text-on-surface-variant">{scopeNote}</span> : null}
      </div>
      <Button variant="ghost" className="shrink-0 px-3" onClick={onClear} aria-label="Снять выбор">
        <X className="size-5" />
        <span className="ml-1.5 hidden sm:inline">Снять</span>
      </Button>
      <Button className="shrink-0" onClick={onPrint}>
        <Printer className="size-5" />
        <span className="ml-1.5">
          <span className="sm:hidden">Печать</span>
          <span className="hidden sm:inline">Печать этикеток</span>
        </span>
      </Button>
    </div>
  );
}
